import * as Yup from 'yup';

import { Payment } from '@uplift/api';
import { EMPTY_STRING, ENGLISH_MATCHES } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { SettingsAddressFormType, settingsAddressValidation, SubmitResult, useWithAddressVerify, useAlert } from '@uplift/ui-v2';
import { useUserBillingQuery } from '@uplift/queries';
import {
  VALIDATION_CARD_CVC, VALIDATION_CARD_EXP_DATE, VALIDATION_CARD_NUMBER
} from '@uplift/validations';

import { useQueryClient } from '@tanstack/react-query';
import { useCurrentuser } from '@uplift/contexts';
import { useSaveBilling } from '../../queries';
import { useBillingContext } from '../billing.context';

export type BillingFormType = {
  payments: null | Payment[];
  cardholderName: string;
  cardNumber: string;
  expirationDate: string;
  cvc: string;
} & SettingsAddressFormType;

export const useBillingForm = () => {
  const me = useCurrentuser();
  const l10n = useL10n();
  const queryClient = useQueryClient();
  const alert = useAlert();
  const { data: billingData } = useUserBillingQuery(me.id);
  const { insertBilling, savePaymentCard } = useSaveBilling();
  const withAddressVerify = useWithAddressVerify();
  const billingContext = useBillingContext();

  const billingFormik = () => {

    const initialValues: BillingFormType = {
      payments: billingContext.paymentsToPay,
      cardholderName: billingData?.billingDetails.name ?? EMPTY_STRING,
      cardNumber: EMPTY_STRING,
      expirationDate: EMPTY_STRING,
      cvc: EMPTY_STRING,
      address1: billingData?.billingDetails.address.line1 ?? EMPTY_STRING,
      address2: billingData?.billingDetails.address.line2 ?? EMPTY_STRING,
      city: billingData?.billingDetails.address.city ?? EMPTY_STRING,
      state: billingData?.billingDetails.address.state ?? EMPTY_STRING,
      zip: billingData?.billingDetails.address.postalCode ?? EMPTY_STRING
    };

    const validationSchema = Yup.object({
      payments: Yup.array().min(1).nullable(),
      cardholderName: Yup.string()
        .matches(ENGLISH_MATCHES, l10n.validations.couldContainEnglish)
        .max(64, l10n.validations.client.nameOnCard.tooLong)
        .required(l10n.validations.client.nameOnCard.empty),
      cardNumber: VALIDATION_CARD_NUMBER,
      expirationDate: VALIDATION_CARD_EXP_DATE,
      cvc: VALIDATION_CARD_CVC,
    }).concat(settingsAddressValidation(l10n));

    const onSubmit = withAddressVerify(async (values: BillingFormType): Promise<SubmitResult> => {
      try {
        const billing = await insertBilling({
          line1: values.address1,
          line2: values.address2,
          city: values.city,
          stateId: values.state,
          zip: values.zip,
        });

        if (!billing) {
          throw new Error();
        }

        const [ month, year ] = CalendarDateService.date(values.expirationDate, 'MM/YY').format('MM-YY').split('-');
        const expMonth = Number(month);
        const expYear = Number(year);
        const payment = await savePaymentCard({
          cardHolderName: values.cardholderName,
          cvc: values.cvc,
          expMonth: expMonth,
          expYear: expYear,
          number: values.cardNumber,
          resourceIds: values.payments?.map((payment) => payment.resourceId) ?? null,
        });

        if (!payment.status) {
          const message = l10n.payments.cardErrors[payment.errorCode] ?? l10n.payments.cardErrors.generic;

          alert.error(message);
          return { success: false };
        }

        await queryClient.invalidateQueries([ 'user', me.id, 'billing' ]);
        alert.success(l10n.accountSettings.changesUpdatedSuccessfully);
        return { success: true };
      } catch (error) {
        alert.error(l10n.accountSettings.changesCouldNotBeSaved);
        return { success: false };
      }
    }, {
      shouldCheck: true,
      initialAddress: {
        address1: initialValues.address1,
        address2: initialValues.address2,
        city: initialValues.city,
        state: initialValues.state,
        zip: initialValues.zip
      }
    });

    return { initialValues, validationSchema, onSubmit };
  };

  return { billingFormik };
};
